/**
 * Consent Manager
 * Handles tracking consent state and integrates with Shopify.customerPrivacy
 * Implements ConsentManagerInterface for CDN script compatibility
 */

import type { ConsentState } from '../types/events';

/**
 * Default time (ms) the SDK waits for a CMP to determine consent before
 * applying the configured fallback. Shared by the SDK builder and the
 * platform-init CMP-timeout guard so both agree on a single value.
 */
export const DEFAULT_CMP_TIMEOUT_MS = 2000;

/**
 * GCM (Google Consent Mode) consent state
 * Matches the structure expected by CDN scripts
 */
export interface GCMConsentState {
  ad_storage: 'granted' | 'denied' | null;
  analytics_storage: 'granted' | 'denied' | null;
  ad_user_data: 'granted' | 'denied' | null;
  ad_personalization: 'granted' | 'denied' | null;
  personalization_storage: 'granted' | 'denied' | null;
  functionality_storage: 'granted';
  security_storage: 'granted';
  wait_for_update: number;
  /** CCPA sale-of-data signal; present on the window.analyzify.current_consent shape. */
  sale_of_data?: 'granted' | 'denied' | null;
}

/**
 * Consent callback type
 */
type ConsentCallback = (consent: GCMConsentState) => void;

/**
 * GCM consent fields plus the `analyzify_consent` summary flag the CDN scripts
 * and GTM tags read (`granted` only when ads and analytics are both granted).
 * `wait_for_update` is left out — it belongs to the gtag consent default.
 */
export function buildConsentFields(consent: Record<string, any>): Record<string, any> {
  const { wait_for_update: _wfu, ...fields } = consent || {};
  return {
    ...fields,
    analyzify_consent:
      fields.ad_storage === 'granted' && fields.analytics_storage === 'granted' ? 'granted' : 'denied',
  };
}

/**
 * Build the `detail` of a status-carrying AnalyzifyConsent event, the shape
 * the Liquid consent layer dispatches: `{ consent, status }` with the GCM
 * fields also flat on `detail`. Used for the consent *fallback/default*
 * dispatches of platform init.
 *
 * Visitor decisions are dispatched flat (see ConsentManager.dispatchConsentEvent):
 * an_gtm.js has its own buffer of ee_ and sh_ pushes (behind the per-store
 * `enableQueuedEvents` flag, which the SDK never sets) that it replays with a
 * `_queue` suffix when it sees `{ consent, status: 'update' }` after a denied
 * state. The SDK's DataLayerProxy is the `_queue` replayer in headless, so a
 * visitor decision must not carry the `consent`/`status` keys — with that flag
 * on, every buffered event would be replayed twice.
 */
export function buildAnalyzifyConsentDetail(
  consent: Record<string, any>,
  status: string
): Record<string, any> {
  const fields = buildConsentFields(consent);
  return { ...fields, consent: fields, status };
}

/**
 * Descriptor for a Consent Management Platform (CMP) integration.
 *
 * Cookiebot is the only first-class CMP today. This shape isolates the
 * CMP-specific bits — the DOM event names it fires and how its consent
 * categories map onto the SDK's analytics/marketing/preferences model — so an
 * additional CMP can be added later by supplying another descriptor, without
 * touching the listener-wiring logic below.
 */
interface CMPAdapter {
  /** Event fired when a returning visitor's stored consent is (re)loaded. */
  readyEvent: string;
  /** Event fired when the visitor accepts consent via the banner. */
  acceptEvent: string;
  /** Event fired when the visitor declines all non-essential categories. */
  declineEvent: string;
  /**
   * Read the CMP's current consent from its global object and map it onto the
   * SDK consent categories. Returns null when the CMP global isn't ready yet.
   */
  read: () => Pick<ConsentState, 'analytics' | 'marketing' | 'preferences'> | null;
}

/**
 * Cookiebot CMP descriptor.
 *   statistics  -> analytics
 *   marketing   -> marketing
 *   preferences -> preferences
 */
const COOKIEBOT_ADAPTER: CMPAdapter = {
  readyEvent: 'CookiebotOnConsentReady',
  acceptEvent: 'CookiebotOnAccept',
  declineEvent: 'CookiebotOnDecline',
  read: () => {
    const cookiebot = (window as any).Cookiebot;
    if (!cookiebot?.consent) return null;
    return {
      analytics: !!cookiebot.consent.statistics,
      marketing: !!cookiebot.consent.marketing,
      preferences: !!cookiebot.consent.preferences,
    };
  },
};

/**
 * ConsentManager class
 * Manages consent state and updates tracking platforms
 * Implements the ConsentManagerInterface expected by window.analyzify.consentManager
 */
/** Every ConsentManager alive on the page (one per SDK instance; see applyFallbackToAll). */
const liveManagers = new Set<ConsentManager>();

export class ConsentManager {
  // Public properties matching ConsentManagerInterface
  public isInitialized = false;
  public consentReady = false;
  public consentQueue: ConsentCallback[] = [];

  /** Whether a CMP (Cookiebot) is configured — used by dispatcher to skip force-granting */
  public hasCMP = false;

  /** Whether the CMP has made an explicit consent determination for this session.
   *  Separate from consentReady — Shopify Privacy API fires visitorConsentCollected
   *  prematurely (before Cookiebot banner interaction), which sets consentReady but
   *  should NOT be treated as a CMP determination. */
  public consentDeterminedByCMP = false;

  // Private state
  private state: ConsentState = {
    analytics: false,
    marketing: false,
    preferences: false,
  };

  /**
   * Whether `state` is a decision — stored from an earlier page or made via
   * setConsent() — as opposed to the default/fallback posture applied when no
   * CMP has answered. A decision is persisted and never overridden by a
   * fallback; a default is replaced by the first decision even when the
   * values are equal.
   */
  private explicitDecision = false;

  private changeCallbacks: ConsentCallback[] = [];
  private cmpDeterminationCallbacks: ((granted: boolean) => void)[] = [];
  private boundConsentHandler: ((event: CustomEvent) => void) | null = null;
  private boundAnalyzifyHandler: ((event: CustomEvent) => void) | null = null;
  private boundCookiebotReadyHandler: (() => void) | null = null;
  private boundCookiebotAcceptHandler: (() => void) | null = null;
  private boundCookiebotDeclineHandler: (() => void) | null = null;
  private boundPrivacyApiLoadedHandler: (() => void) | null = null;
  private boundVisitorConsentHandler: ((event: Event) => void) | null = null;
  private isDispatchingEvents = false; // Prevent re-entrancy
  private _isSyncingToShopify = false; // Prevent consent loop: setConsent → syncToShopifyPrivacyAPI → event → setConsent
  private lastPushedGCM: string | null = null; // Serialized last-pushed consent state for dedup

  // Headless Shopify Privacy API config
  private checkoutRootDomain?: string;
  private storefrontRootDomain?: string;
  private storefrontAccessToken?: string;

  constructor(
    cookiebotEnabled?: boolean,
    checkoutRootDomain?: string,
    storefrontRootDomain?: string,
    storefrontAccessToken?: string
  ) {
    this.hasCMP = !!cookiebotEnabled;
    this.checkoutRootDomain = checkoutRootDomain;
    this.storefrontRootDomain = storefrontRootDomain;
    this.storefrontAccessToken = storefrontAccessToken;
    this.loadStoredConsent();
    this.setupEventListeners();
    if (cookiebotEnabled) {
      this.setupCookiebotListeners();
    }
    if (storefrontRootDomain) {
      this.setupShopifyPrivacyApiListener();
    }
    // Seed lastPushedGCM with initial state so updateGCM() skips
    // redundant pushes that match what script-loader pushes as consent → default
    const { wait_for_update: _, ...initialState } = this.getGCMState();
    this.lastPushedGCM = JSON.stringify(initialState);
    this.isInitialized = true;
    liveManagers.add(this);
  }

  /**
   * Apply the CMP-timeout / no-CMP fallback to every SDK instance on the page.
   * Platform init runs once (from the instance that loaded the CDN scripts),
   * but a React re-mount or StrictMode creates further instances that reuse
   * that initialization and must see the same consent state.
   */
  static applyFallbackToAll(granted: boolean): void {
    for (const manager of liveManagers) {
      manager.applyFallbackConsent(granted);
    }
  }

  /**
   * Cleanup resources - removes event listeners
   * Should be called when the SDK is being destroyed
   */
  destroy(): void {
    liveManagers.delete(this);
    if (typeof window === 'undefined') return;

    if (this.boundConsentHandler) {
      window.removeEventListener(
        'Shopify:CustomerPrivacy:consentUpdated',
        this.boundConsentHandler as EventListener
      );
      this.boundConsentHandler = null;
    }

    if (this.boundAnalyzifyHandler) {
      window.removeEventListener(
        'AnalyzifyConsent',
        this.boundAnalyzifyHandler as EventListener
      );
      this.boundAnalyzifyHandler = null;
    }

    if (this.boundCookiebotReadyHandler) {
      window.removeEventListener(COOKIEBOT_ADAPTER.readyEvent, this.boundCookiebotReadyHandler);
      this.boundCookiebotReadyHandler = null;
    }
    if (this.boundCookiebotAcceptHandler) {
      window.removeEventListener(COOKIEBOT_ADAPTER.acceptEvent, this.boundCookiebotAcceptHandler);
      this.boundCookiebotAcceptHandler = null;
    }
    if (this.boundCookiebotDeclineHandler) {
      window.removeEventListener(COOKIEBOT_ADAPTER.declineEvent, this.boundCookiebotDeclineHandler);
      this.boundCookiebotDeclineHandler = null;
    }

    if (this.boundPrivacyApiLoadedHandler) {
      document.removeEventListener(
        'consentTrackingApiLoaded',
        this.boundPrivacyApiLoadedHandler
      );
      this.boundPrivacyApiLoadedHandler = null;
    }

    if (this.boundVisitorConsentHandler) {
      document.removeEventListener(
        'visitorConsentCollected',
        this.boundVisitorConsentHandler
      );
      this.boundVisitorConsentHandler = null;
    }

    this.changeCallbacks = [];
    this.cmpDeterminationCallbacks = [];
    this.consentQueue = [];
    this.isInitialized = false;
    this.consentReady = false;
    this.consentDeterminedByCMP = false;
    this.lastPushedGCM = null;
  }

  /**
   * Load consent from storage
   */
  private loadStoredConsent(): void {
    if (typeof window === 'undefined') return;

    try {
      const stored = localStorage.getItem('analyzify_consent');
      if (stored) {
        this.state = JSON.parse(stored);
        // A stored value is a visitor decision from an earlier page
        this.consentReady = true;
        this.explicitDecision = true;
      }
    } catch {
      // Ignore storage errors
    }
  }

  /**
   * Save consent to storage
   */
  private saveConsent(): void {
    if (typeof window === 'undefined') return;

    try {
      localStorage.setItem('analyzify_consent', JSON.stringify(this.state));
    } catch {
      // Ignore storage errors
    }
  }

  /**
   * Setup event listeners for consent updates
   */
  private setupEventListeners(): void {
    if (typeof window === 'undefined') return;

    // Store bound handler reference for cleanup - Shopify Customer Privacy API
    // Once CMP (Cookiebot) determines consent, it is authoritative — ignore Shopify
    // API events to prevent a ping-pong loop where SDK syncs to Shopify API, Shopify
    // fires back with region-based overrides, SDK syncs again, etc.
    this.boundConsentHandler = (event: CustomEvent) => {
      if (this.consentDeterminedByCMP) return; // CMP is authoritative
      if (this._isSyncingToShopify) return;
      const detail = event.detail;
      if (detail) {
        this.setConsent({
          analytics: detail.analytics,
          marketing: detail.marketing,
          preferences: detail.preferences,
          sale_of_data: detail.saleOfData,
        });
      }
    };

    // Listen for Shopify customer privacy updates
    window.addEventListener(
      'Shopify:CustomerPrivacy:consentUpdated',
      this.boundConsentHandler as EventListener
    );

    // Also listen for AnalyzifyConsent event (used by extension's consent banner)
    // Skip if we're the ones dispatching to avoid infinite loop
    this.boundAnalyzifyHandler = (event: CustomEvent) => {
      if (this.isDispatchingEvents) return; // Skip our own events
      const detail = event.detail;
      if (detail) {
        // AnalyzifyConsent event provides GCM-style consent — either flat on
        // detail or wrapped as { consent, status } (Liquid's shape)
        const gcmConsent = (detail.consent || detail) as Partial<GCMConsentState>;
        this.setConsent({
          analytics: gcmConsent.analytics_storage === 'granted',
          marketing: gcmConsent.ad_storage === 'granted',
          preferences: gcmConsent.personalization_storage === 'granted',
        });
      }
    };

    window.addEventListener(
      'AnalyzifyConsent',
      this.boundAnalyzifyHandler as EventListener
    );
  }

  /**
   * Read consent state from the Cookiebot global object.
   * Maps Cookiebot consent categories to SDK consent state.
   *
   * Cookiebot.consent categories:
   *   statistics → analytics
   *   marketing  → marketing
   *   preferences → preferences
   */
  private readCookiebotConsent(): void {
    const consent = COOKIEBOT_ADAPTER.read();
    if (!consent) return;
    this.setConsent(consent);
  }

  /**
   * Setup Cookiebot CMP event listeners.
   * These fire when the user interacts with the Cookiebot consent banner
   * or when a returning user's stored consent is loaded.
   */
  private setupCookiebotListeners(): void {
    if (typeof window === 'undefined') return;

    // CookiebotOnConsentReady — fires for returning users with stored preferences
    this.boundCookiebotReadyHandler = () => {
      this.readCookiebotConsent();
      this.setCMPDetermined();
    };
    window.addEventListener(COOKIEBOT_ADAPTER.readyEvent, this.boundCookiebotReadyHandler);

    // CookiebotOnAccept — fires when user clicks accept on the banner
    this.boundCookiebotAcceptHandler = () => {
      this.readCookiebotConsent();
      this.setCMPDetermined();
    };
    window.addEventListener(COOKIEBOT_ADAPTER.acceptEvent, this.boundCookiebotAcceptHandler);

    // CookiebotOnDecline — fires when user declines all cookies
    this.boundCookiebotDeclineHandler = () => {
      this.denyAll();
      this.setCMPDetermined();
    };
    window.addEventListener(COOKIEBOT_ADAPTER.declineEvent, this.boundCookiebotDeclineHandler);
  }

  /**
   * Setup listener for when Shopify's consent-tracking-api.js finishes loading.
   * Handles the race condition where Cookiebot fires consent before the API is ready.
   */
  private setupShopifyPrivacyApiListener(): void {
    if (typeof document === 'undefined') return;

    this.boundPrivacyApiLoadedHandler = () => {
      // Re-sync consent if we already have a consent decision
      if (this.consentReady) {
        this.syncToShopifyPrivacyAPI();
      }
    };

    document.addEventListener(
      'consentTrackingApiLoaded',
      this.boundPrivacyApiLoadedHandler
    );

    // Also listen for Shopify's real visitorConsentCollected event
    // Fired by consent-tracking-api.js when the visitor's consent is determined.
    // Same CMP-authoritative guard as boundConsentHandler above.
    this.boundVisitorConsentHandler = (event: Event) => {
      if (this.consentDeterminedByCMP) return; // CMP is authoritative
      if (this._isSyncingToShopify) return;
      const detail = (event as CustomEvent).detail;
      if (detail) {
        this.setConsent({
          analytics: !!detail.analyticsAllowed,
          marketing: !!detail.marketingAllowed,
          preferences: !!detail.preferencesAllowed,
          sale_of_data: !!detail.saleOfDataAllowed,
        });
      }
    };

    document.addEventListener(
      'visitorConsentCollected',
      this.boundVisitorConsentHandler
    );
  }

  /**
   * Sync consent state to Shopify's real Customer Privacy API.
   * This sets the consent cookies that Shopify's checkout reads.
   * Only works when the real consent-tracking-api.js is loaded (not the shim).
   */
  private syncToShopifyPrivacyAPI(): void {
    if (typeof window === 'undefined') return;
    if (!this.storefrontRootDomain) return;

    const shopifyPrivacy = (window as any).Shopify?.customerPrivacy;
    if (!shopifyPrivacy?.setTrackingConsent) return;

    // Set flag to prevent processing Shopify API events triggered by our own sync.
    // Without this, a consent loop occurs: setConsent → syncToShopifyPrivacyAPI →
    // Shopify fires visitorConsentCollected/consentUpdated → setConsent → ...
    this._isSyncingToShopify = true;

    shopifyPrivacy.setTrackingConsent(
      {
        analytics: this.state.analytics,
        marketing: this.state.marketing,
        preferences: this.state.preferences,
        sale_of_data: this.state.marketing,
        headlessStorefront: true,
        checkoutRootDomain: this.checkoutRootDomain || this.storefrontRootDomain,
        storefrontRootDomain: this.storefrontRootDomain,
        storefrontAccessToken: this.storefrontAccessToken,
      },
      (result?: { error?: string }) => {
        this._isSyncingToShopify = false;
        if (result?.error) {
          console.warn('[Analyzify] Shopify Privacy API setTrackingConsent error:', result.error);
        }
      }
    );

    // Safety: clear flag after timeout in case callback never fires
    setTimeout(() => { this._isSyncingToShopify = false; }, 3000);
  }

  /**
   * Queue a consent action to be executed when consent is ready
   * Part of ConsentManagerInterface
   */
  queueConsentAction(callback: ConsentCallback): void {
    if (this.consentReady) {
      // Consent already ready, execute immediately
      callback(this.getGCMState());
    } else {
      // Queue for later execution
      this.consentQueue.push(callback);
    }
  }

  /**
   * Process all queued consent actions
   * Part of ConsentManagerInterface
   */
  processConsentQueue(): void {
    const gcmState = this.getGCMState();

    // Execute all queued callbacks
    while (this.consentQueue.length > 0) {
      const callback = this.consentQueue.shift();
      if (callback) {
        try {
          callback(gcmState);
        } catch (error) {
          console.warn('[ConsentManager] Error processing queued action:', error);
        }
      }
    }
  }

  /**
   * Register a callback for consent changes
   * Part of ConsentManagerInterface
   */
  onChange(callback: ConsentCallback): () => void {
    this.changeCallbacks.push(callback);
    return () => {
      this.changeCallbacks = this.changeCallbacks.filter((cb) => cb !== callback);
    };
  }

  /**
   * Dispatch consent update to CDN scripts
   * Part of ConsentManagerInterface (optional)
   */
  dispatchUpdate(consent: GCMConsentState, status: string): void {
    if (typeof window === 'undefined') return;

    // Update window.analyzify if available
    const analyzify = (window as any).analyzify;
    if (analyzify) {
      analyzify.current_consent = consent;
      analyzify.consent_flag = {
        ...analyzify.consent_flag,
        update: true,
      };
    }

    // Dispatch AnalyzifyConsent event (matches extension: Liquid dispatches on document)
    const detail = buildAnalyzifyConsentDetail(consent, status);
    this.isDispatchingEvents = true;
    try {
      window.dispatchEvent(new CustomEvent('AnalyzifyConsent', { detail }));
      document.dispatchEvent(new CustomEvent('AnalyzifyConsent', { detail }));
    } finally {
      this.isDispatchingEvents = false;
    }
  }

  /**
   * Set consent state
   */
  setConsent(consent: Partial<ConsentState>): void {
    // Skip if state hasn't changed — prevents infinite loop:
    // setConsent → syncToShopifyPrivacyAPI → visitorConsentCollected → setConsent
    // Only a decision equal to the current decision is a no-op: a decision
    // equal to the default/fallback posture must still be persisted, pushed
    // to Google Consent Mode and synced to Shopify.
    const merged = { ...this.state, ...consent };
    if (
      this.consentReady &&
      this.explicitDecision &&
      merged.analytics === this.state.analytics &&
      merged.marketing === this.state.marketing &&
      merged.preferences === this.state.preferences &&
      merged.sale_of_data === this.state.sale_of_data
    ) {
      return;
    }

    this.state = merged;
    this.explicitDecision = true;
    this.saveConsent();

    // Publish the new state on window.analyzify.current_consent before any
    // queued CDN action runs — the builder's consent queue hands
    // current_consent to its callbacks, which would otherwise see the
    // pre-decision (all denied) state.
    this.syncCurrentConsent();

    // Mark consent as ready
    if (!this.consentReady) {
      this.consentReady = true;
      // Process any queued actions
      this.processConsentQueue();
    }

    // Sync consentReady to the builder's consent manager so the CMP timeout
    // in platform-init.ts sees the correct state and doesn't force-grant.
    // Also process the builder's queued actions — without this, actions queued
    // via analyzify.consentManager.queueConsentAction() would be stranded
    // because the fallback timeout (which previously called processConsentQueue)
    // is now correctly skipped.
    if (typeof window !== 'undefined') {
      const builderCM = (window as any).analyzify?.consentManager;
      if (builderCM && builderCM !== this && !builderCM.consentReady) {
        builderCM.consentReady = true;
        builderCM.processConsentQueue();
      }
    }

    // Update GCM
    this.updateGCM();

    // Sync to Shopify's real Privacy API (sets cookies for checkout)
    this.syncToShopifyPrivacyAPI();

    // Notify change callbacks
    this.notifyChangeCallbacks();

    // Dispatch consent event
    this.dispatchConsentEvent();
  }

  /**
   * Apply the default consent posture for a store without a consent decision.
   *
   * Used when no CMP is configured: tracking proceeds per
   * `consentDefaults.timeoutFallback` ('grant' by default). Unlike setConsent()
   * this is not a visitor choice — it is not persisted, not synced to
   * Shopify's Privacy API and not pushed to Google Consent Mode. An explicit
   * setConsent() (host banner, Shopify Privacy API) still overrides it.
   */
  applyDefaultConsent(granted: boolean): void {
    if (this.consentReady) return; // A stored or explicit decision wins
    this.applyNonVisitorConsent(granted);
  }

  /**
   * Apply the CMP-timeout / no-CMP fallback chosen by platform init
   * (`consentDefaults.timeoutFallback`). Keeps the SDK state — canTrack(),
   * the consentSettings injected into dataLayer pushes and the event buffer —
   * in line with what the CDN scripts were told. Not persisted, so the CMP's
   * real decision on a later page load still applies. A determination the CMP
   * already made is never overridden.
   */
  applyFallbackConsent(granted: boolean): void {
    // A decision — stored, made through setConsent(), or determined by the
    // CMP — is never overridden by a fallback.
    if (this.consentDeterminedByCMP || this.explicitDecision) return;
    this.applyNonVisitorConsent(granted);
    this.notifyChangeCallbacks();
  }

  private applyNonVisitorConsent(granted: boolean): void {
    this.state = {
      ...this.state,
      analytics: granted,
      marketing: granted,
      preferences: granted,
    };
    this.syncCurrentConsent();
    if (!this.consentReady) {
      this.consentReady = true;
      this.processConsentQueue();
    }
  }

  /**
   * Mirror the SDK consent state onto window.analyzify.current_consent, the
   * object CDN scripts and the GTM consentUpdate push read.
   */
  private syncCurrentConsent(): void {
    if (typeof window === 'undefined') return;
    const analyzify = (window as any).analyzify;
    if (!analyzify) return;
    analyzify.current_consent = { ...analyzify.current_consent, ...buildConsentFields(this.getGCMState()) };
  }

  /**
   * Get current consent state
   */
  getConsent(): ConsentState {
    return { ...this.state };
  }

  /**
   * Get GCM-formatted consent state
   */
  getGCMState(): GCMConsentState {
    return {
      ad_storage: this.state.marketing ? 'granted' : 'denied',
      analytics_storage: this.state.analytics ? 'granted' : 'denied',
      ad_user_data: this.state.marketing ? 'granted' : 'denied',
      ad_personalization: this.state.marketing ? 'granted' : 'denied',
      personalization_storage: this.state.preferences ? 'granted' : 'denied',
      functionality_storage: 'granted',
      security_storage: 'granted',
      wait_for_update: 500,
    };
  }

  /**
   * Check if user can be tracked
   */
  canTrack(): boolean {
    return this.state.analytics || this.state.marketing;
  }

  /**
   * Check if analytics tracking is allowed
   */
  analyticsAllowed(): boolean {
    return this.state.analytics;
  }

  /**
   * Check if marketing tracking is allowed
   */
  marketingAllowed(): boolean {
    return this.state.marketing;
  }

  /**
   * Notify all registered change callbacks
   */
  private notifyChangeCallbacks(): void {
    const gcmState = this.getGCMState();
    for (const callback of this.changeCallbacks) {
      try {
        callback(gcmState);
      } catch (error) {
        console.warn('[ConsentManager] Error in change callback:', error);
      }
    }
  }

  /**
   * Update Google Consent Mode (GCM)
   */
  private updateGCM(): void {
    if (typeof window === 'undefined') return;

    const gtag = (window as any).gtag;
    if (typeof gtag !== 'function') return;

    const gcmState = this.getGCMState();

    // Build consent update state WITHOUT wait_for_update
    // (wait_for_update is only valid in consent → default per Google's spec)
    const { wait_for_update: _, ...updateState } = gcmState;

    // Skip if consent state hasn't changed since last push
    const serialized = JSON.stringify(updateState);
    if (serialized === this.lastPushedGCM) return;
    this.lastPushedGCM = serialized;

    gtag('consent', 'update', updateState);

    // Also update window.analyzify if available
    const analyzify = (window as any).analyzify;
    if (analyzify) {
      analyzify.current_consent = gcmState;
    }
  }

  /**
   * Dispatch consent update event
   * Uses 'AnalyzifyConsent' to match extension behavior
   */
  private dispatchConsentEvent(): void {
    if (typeof window === 'undefined') return;

    // Set flag to prevent re-entrancy (we listen to AnalyzifyConsent too)
    this.isDispatchingEvents = true;

    try {
      const gcmState = this.getGCMState();
      // Flat GCM fields + analyzify_consent. Deliberately NOT the Liquid
      // { consent, status } shape: see buildAnalyzifyConsentDetail — the
      // DataLayerProxy is the single `_queue` replayer in headless.
      const detail = buildConsentFields(gcmState);

      // Dispatch AnalyzifyConsent event (matches extension)
      window.dispatchEvent(new CustomEvent('AnalyzifyConsent', { detail }));

      // Also dispatch on document for CDN scripts (an_analyzify.js consentHandler,
      // the builder consentManager.onChange subscribers, an_gtag_ss.js).
      // CDN listens on document because Liquid themes dispatch there (the Analyzify consent layer).
      document.dispatchEvent(new CustomEvent('AnalyzifyConsent', { detail }));

      // Also dispatch analyzify:consent:updated for SDK consumers
      window.dispatchEvent(
        new CustomEvent('analyzify:consent:updated', {
          detail: { ...this.state },
        })
      );

      // Push consentUpdate to GTM dataLayer.
      // In Liquid themes, the Analyzify consent layer listens for AnalyzifyConsent and pushes
      // this event to dataLayer. In headless, that snippet isn't loaded, so the SDK
      // must push it directly. This is the CMP-agnostic consent signal that GTM
      // Trigger Groups use — it works regardless of which CMP the store uses
      // (Cookiebot, OneTrust, Shopify native, or none).
      // NOTE: No gcm_active guard — this is a dataLayer event for GTM Trigger Groups,
      // NOT a Google Consent Mode call. It must always be pushed.
      const win = window as any;
      if (win.dataLayer) {
        // Add analyzify_consent flag matching the Analyzify consent layer behavior
        const consentWithFlag = {
          ...gcmState,
          analyzify_consent: (
            gcmState.ad_storage === 'granted' &&
            gcmState.analytics_storage === 'granted'
          ) ? 'granted' : 'denied',
        };

        win.dataLayer.push({
          event: 'consentUpdate',
          status: 'update',
          consentSettings: consentWithFlag,
        });
      }
    } finally {
      this.isDispatchingEvents = false;
    }
  }

  /**
   * Register a callback for when the CMP makes an explicit consent determination.
   * If the CMP has already determined consent, the callback fires immediately.
   */
  onCMPDetermination(callback: (granted: boolean) => void): void {
    if (this.consentDeterminedByCMP) {
      callback(this.canTrack());
    } else {
      this.cmpDeterminationCallbacks.push(callback);
    }
  }

  /**
   * Mark CMP as having made an explicit determination and fire callbacks.
   * Called from Cookiebot event handlers and CMP timeout fallback.
   */
  private setCMPDetermined(): void {
    if (this.consentDeterminedByCMP) return;
    this.consentDeterminedByCMP = true;
    const granted = this.canTrack();
    for (const cb of this.cmpDeterminationCallbacks) {
      try {
        cb(granted);
      } catch (error) {
        console.warn('[ConsentManager] Error in CMP determination callback:', error);
      }
    }
    this.cmpDeterminationCallbacks = [];
  }

  /**
   * Start a CMP timeout fallback. If the CMP hasn't determined consent
   * within the given time, grant all consent and mark as determined.
   */
  startCMPTimeout(ms: number): void {
    if (!this.hasCMP || this.consentDeterminedByCMP) return;
    setTimeout(() => {
      if (!this.consentDeterminedByCMP) {
        this.grantAll();
        this.setCMPDetermined();
      }
    }, ms);
  }

  /**
   * Grant all consent
   */
  grantAll(): void {
    this.setConsent({
      analytics: true,
      marketing: true,
      preferences: true,
    });
  }

  /**
   * Deny all consent (except functionality)
   */
  denyAll(): void {
    this.setConsent({
      analytics: false,
      marketing: false,
      preferences: false,
    });
  }
}
